import { Outlet, Route, Routes } from 'react-router-dom';
import { CssBaseline, Stack, Box } from '@mui/material';
import { Suspense, lazy } from 'react';
import { Body1, PrimaryNav, Loading, Toast } from '../components';
import { ROUTE } from '../constants/general';
import { useLoadingAtom } from '../atoms';
import { useToastAtom } from '../atoms/toastAtom';
import { NoLead } from '../components/NoLead';
import { AuthGuard } from '../components/AuthGuard';

const NotFoundPage = lazy(() => import('./NotFound').then(module => ({ default: module.NotFoundPage })));
const HomePage = lazy(() => import('./Home').then(module => ({ default: module.HomePage })));
const LoginPage = lazy(() => import('./LoginPage').then(module => ({ default: module.LoginPage })));
const ForgotPasswordPage = lazy(() => import('./ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./ResetPasswordPage').then(module => ({ default: module.ResetPasswordPage })));
const LeadPage = lazy(() => import('./LeadPage').then(module => ({ default: module.LeadPage })));

const LeadLayout = lazy(() => import('../components/layout/LeadLayout').then(module => ({ default: module.LeadLayout })));
const PropertyLayout = lazy(() => import('../components/layout/PropertyLayout').then(module => ({ default: module.PropertyLayout })));
const PersonalInfoLayout = lazy(() => import('../components/layout/PersonalInfoLayout').then(module => ({ default: module.PersonalInfoLayout })));
// Temporarily hidden for future use: Design Assistant tab
// const DesignAssistantLayout = lazy(() => import('../components/layout/DesignAssistantLayout').then(module => ({ default: module.DesignAssistantLayout })));

export function AppRoutes() {
  const { isLoading } = useLoadingAtom();
  const { toast } = useToastAtom();

  return (
    <>
      <CssBaseline />
      <Suspense fallback={<Loading loading sx={{ backgroundColor: 'common.white' }} />}>
        <Loading loading={isLoading} />
        <Stack height="100vh">
          <PrimaryNav />
          <Box flex={1} height="90%">
            <Routes>
              <Route path={ROUTE.home} element={<HomePage />} />
              <Route path={ROUTE.signin} element={<LoginPage />} />
              <Route path={ROUTE.forgotPassword} element={<ForgotPasswordPage />} />
              <Route path={ROUTE.resetPassword} element={<ResetPasswordPage />} />
              <Route path={ROUTE.noLead} element={<NoLead />} />
              <Route path={`${ROUTE.leads}/*`} element={<AuthGuard component={<Outlet />} />}>
                <Route path={`${ROUTE.leadId}`} element={<LeadPage />}>
                  <Route path={`${ROUTE.project}`} element={<LeadLayout />} />
                  <Route path={`${ROUTE.property}`} element={<PropertyLayout />} />
                  <Route path={`${ROUTE.personal}`} element={<PersonalInfoLayout />} />
                  {/* Temporarily hidden for future use: Design Assistant tab */}
                  {/* <Route path={`${ROUTE.designAssistant}`} element={<DesignAssistantLayout />} /> */}
                </Route>
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Box>
          <Toast open={toast.open}>
            <Body1 locale={toast.message} />
          </Toast>
        </Stack>
      </Suspense>
    </>
  );
}
